import React, { useState, useCallback, useMemo, createContext, useContext, useEffect } from 'react';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { ArrowUpRightIcon, ArrowLeftIcon } from 'lucide-react';
import InfiniteScroll from 'react-infinite-scroll-component';
import _chunk from 'lodash/chunk';

import { companyTagsApi } from 'api';
import { CompanyTag } from 'types/company.types';
import { Checkbox } from '@epoch-ui/components/Checkbox';
import { Label } from '@epoch-ui/components/Form';
import { Button } from '@epoch-ui/components/Button';
import { ButtonLink } from '@epoch-ui/components/ButtonLink';
import { SearchInput } from '@epoch-ui/components/SearchInput';
import { Separator } from '@epoch-ui/components/Separator';
import { Skeleton } from '@epoch-ui/components/Skeleton/skeleton';
import { Toggle } from '@epoch-ui/components/toggle';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalFooter,
  ModalDescription
} from '@epoch-ui/components/Modal/modal';

import { useEditableEvent } from '../../../../hooks/useEditableEvent';

type EditCategoriesModalView = 'mostSelected' | 'moreCategories';

interface ManageEventCategoriesContextValue {
  selectedCategories: CompanyTag[];
  addCategory: (category: CompanyTag) => void;
  removeCategory: (category: CompanyTag) => void;
  setCurrentView: (step: EditCategoriesModalView) => void;
  isCategorySelected: (category: CompanyTag) => boolean;
}

const EditCategoriesContext = createContext<ManageEventCategoriesContextValue>({
  selectedCategories: [],
  addCategory: () => {
    /**/
  },
  removeCategory: () => {
    /**/
  },
  setCurrentView: () => {
    /**/
  },
  isCategorySelected: () => false
});

interface ManageEventCategoriesModalProps extends React.ComponentProps<typeof Modal> {
  onSubmit: (categories: CompanyTag[]) => void | Promise<unknown>;
}

const ManageEventCategoriesModal: React.FC<ManageEventCategoriesModalProps> = ({
  onSubmit,
  onOpenChange,
  ...props
}) => {
  const event = useEditableEvent();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [currentView, setCurrentView] = useState<EditCategoriesModalView>('mostSelected');
  const [selectedCategories, setSelectedCategories] = useState<CompanyTag[]>(
    event.categories || []
  );

  const addCategory = useCallback((category: CompanyTag) => {
    setSelectedCategories((prev) => [...prev, category]);
  }, []);

  const removeCategory = useCallback((category: CompanyTag) => {
    setSelectedCategories((prev) => prev.filter((c) => c.id !== category.id));
  }, []);

  const isCategorySelected = useCallback(
    (category: CompanyTag) => {
      return selectedCategories.some((c) => c.id === category.id);
    },
    [selectedCategories]
  );

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    try {
      await onSubmit(selectedCategories);
    } finally {
      setIsSubmitting(false);
    }
  }, [onSubmit, selectedCategories]);

  const ctxValue = useMemo(
    () => ({
      selectedCategories,
      addCategory,
      removeCategory,
      setCurrentView,
      isCategorySelected
    }),
    [selectedCategories, addCategory, removeCategory, setCurrentView, isCategorySelected]
  );

  // Check if selected IDs and initial IDs differ (detects adds/removals)
  const initialIds = useMemo(
    () => new Set((event.categories || []).map((c) => Number(c.id))),
    [event.categories]
  );
  const selectedIds = useMemo(
    () => new Set(selectedCategories.map((c) => Number(c.id))),
    [selectedCategories]
  );
  const isDirty = useMemo(() => {
    if (initialIds.size !== selectedIds.size) return true;

    const initialArr = Array.from(initialIds);
    const selectedArr = Array.from(selectedIds);

    // Check if any IDs are in selected but not in initial
    if (selectedArr.some((id) => !initialIds.has(id))) return true;
    // Check if any IDs are in initial but not in selected
    if (initialArr.some((id) => !selectedIds.has(id))) return true;
    return false;
  }, [initialIds, selectedIds]);

  // Effect to reset state when event object changes
  useEffect(() => {
    setSelectedCategories(event.categories || []);
  }, [event.categories]);

  return (
    <EditCategoriesContext.Provider value={ctxValue}>
      <Modal
        {...props}
        onOpenChange={(open) => {
          onOpenChange?.(open);

          // Reset state when modal is closed
          if (!open) {
            setCurrentView('mostSelected');
            setSelectedCategories(event.categories || []);
          }
        }}
      >
        <ModalContent size="md" className=" tw-transition-all">
          {currentView === 'mostSelected' && <MostSelectedCategoriesView />}
          {currentView === 'moreCategories' && <MoreCategoriesView />}

          <ModalFooter>
            <Button
              className="tw-w-full"
              onClick={handleSubmit}
              disabled={isSubmitting || !isDirty}
              loading={isSubmitting}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </EditCategoriesContext.Provider>
  );
};

const MostSelectedCategoriesView: React.FC = () => {
  const event = useEditableEvent();
  const { setCurrentView, isCategorySelected, addCategory, removeCategory } =
    useContext(EditCategoriesContext);

  const mostSelectedTagsQuery = useQuery({
    queryKey: ['mostSelectedCompanyTags', event.companyId],
    queryFn: () => companyTagsApi.listMostSelected(event.companyId!),
    enabled: !!event.companyId
  });

  // Chunked into arrays of up to 4 tags each; sorted by name
  const mostSelectedTagsChunks = useMemo(() => {
    return _chunk(
      mostSelectedTagsQuery.data?.sort((a, b) => a.name.localeCompare(b.name)),
      // Chunk size
      4
    );
  }, [mostSelectedTagsQuery.data]);

  return (
    <React.Fragment>
      <ModalHeader>
        <ModalTitle>Categories</ModalTitle>
        <ModalDescription className="tw-mt-2">
          Improve event discovery by allowing employees to filter events by category and connecting
          categories to Discover tabs.
        </ModalDescription>
      </ModalHeader>

      <div>
        <div className="tw-mb-3 tw-flex tw-flex-col tw-flex-wrap tw-gap-2">
          {mostSelectedTagsQuery.isLoading && (
            <>
              <Skeleton className="tw-h-8 tw-w-12" />
              <Skeleton className="tw-h-8 tw-w-8" />
              <Skeleton className="tw-h-8 tw-w-20" />
            </>
          )}

          {mostSelectedTagsChunks?.map((chunk) => (
            <div
              key={chunk.map((tag) => tag.id).join(',')}
              className="tw-flex tw-flex-row tw-gap-2"
            >
              {chunk.map((tag) => (
                <Toggle
                  key={tag.id}
                  value={tag.id}
                  className="tw-w-fit tw-px-2 tw-py-[6px]"
                  pressed={isCategorySelected(tag)}
                  onPressedChange={(pressed) => (pressed ? addCategory(tag) : removeCategory(tag))}
                >
                  {tag.name}
                </Toggle>
              ))}
            </div>
          ))}
        </div>

        <ButtonLink onClick={(): void => setCurrentView('moreCategories')}>
          See more <ArrowUpRightIcon size={16} />
        </ButtonLink>
      </div>
    </React.Fragment>
  );
};

const CategoryItemSkeleton: React.FC = () => (
  <div className="tw-pt-4">
    <div className="tw-mb-4 tw-flex tw-flex-row tw-items-center tw-justify-start tw-gap-3">
      <Skeleton className="tw-h-4 tw-w-4" />
      <Skeleton className="tw-h-4 tw-w-20" />
    </div>
    <Separator />
  </div>
);

const MoreCategoriesView: React.FC = () => {
  const event = useEditableEvent();
  const { setCurrentView, addCategory, removeCategory, isCategorySelected } =
    useContext(EditCategoriesContext);

  const [searchText, setSearchText] = useState<string>('');

  const paginatedTagsQuery = useInfiniteQuery({
    queryKey: ['paginatedCompanyTags', event.companyId, searchText],
    queryFn: ({ pageParam = 1 }) =>
      companyTagsApi.listPaginated({
        companyId: event.companyId!,
        page: pageParam,
        perPage: 10,
        searchText
      }),
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.pages ? lastPage.page + 1 : undefined, // Returned value will be used to determine `hasNextPage`
    initialPageParam: 1,
    enabled: !!event.companyId
  });

  const tags = useMemo(() => {
    return paginatedTagsQuery.data?.pages.flatMap((page) => page.items) ?? [];
  }, [paginatedTagsQuery.data]);

  return (
    <React.Fragment>
      <ModalHeader>
        <Button
          variant="link"
          size="xxs"
          className="tw-mb-2 tw-justify-start tw-p-0 tw-text-sm tw-leading-5 tw-text-medium-grey tw-no-underline hover:tw-underline"
          onClick={(): void => setCurrentView('mostSelected')}
        >
          <ArrowLeftIcon size={24} className="tw-mr-1" /> Categories
        </Button>
        <ModalTitle>More Categories</ModalTitle>
      </ModalHeader>

      <div>
        <SearchInput
          className="tw-w-full"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />

        <div
          id="paginated-categories-list"
          className="tw-mt-1 tw-flex tw-max-h-80 tw-min-h-80 tw-flex-col tw-gap-2 tw-overflow-y-auto"
        >
          {/* Loading state */}
          {paginatedTagsQuery.isLoading && (
            <>
              <CategoryItemSkeleton />
              <CategoryItemSkeleton />
              <CategoryItemSkeleton />
            </>
          )}

          {/* Empty search results */}
          {!paginatedTagsQuery.isLoading &&
            searchText !== '' &&
            paginatedTagsQuery.data?.pages[0].items.length === 0 && (
              <p className="tw-mx-auto tw-pt-6 tw-text-sm tw-font-normal tw-leading-5">
                No tags found
              </p>
            )}

          {/* Categories list */}
          <InfiniteScroll
            next={paginatedTagsQuery.fetchNextPage}
            loader={<CategoryItemSkeleton />}
            dataLength={tags.length}
            hasMore={paginatedTagsQuery.hasNextPage}
            scrollableTarget="paginated-categories-list"
          >
            {tags.map((tag) => (
              <div key={tag.id} className="tw-pt-4">
                <div className="tw-mb-4 tw-flex tw-flex-row tw-items-center tw-justify-start tw-gap-3">
                  <Checkbox
                    id={`category-${tag.id}`}
                    checked={isCategorySelected(tag)}
                    onCheckedChange={(checked) =>
                      checked ? addCategory(tag) : removeCategory(tag)
                    }
                  />
                  <Label
                    htmlFor={`category-${tag.id}`}
                    className="tw-m-0 tw-text-sm tw-font-normal tw-leading-5"
                  >
                    {tag.name}
                  </Label>
                </div>
                <Separator />
              </div>
            ))}
          </InfiniteScroll>
        </div>
      </div>
    </React.Fragment>
  );
};

export { ManageEventCategoriesModal };
